import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_PULSE_PRESET_ID,
  PULSE_PRESET_CATALOG,
  SURF_PRESENTATION_TYPES,
  analyzeSurfStyleReference,
  defaultStyleAspects,
  expandSurfStylePreset,
  generateSurfStylePreviewImage,
  isSurfPreviewLegible,
  listSurfStyleTemplates,
  previewSampleForPresentationType,
  resolveSurfPreviewImageUrl,
  saveSurfStyleTemplate,
  deleteSurfStyleTemplate,
  styleFromPreset,
  type SurfStyle,
  type SurfStyleTemplateRecord,
} from "@vida/surf-core";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { EmptyState } from "../../ui/EmptyState";
import {
  PaletteEditor,
  applyPaletteArrays,
  derivePaletteFromStyle,
} from "./PaletteEditor";
import { StylePreviewFrame } from "./StylePreviewFrame";
import {
  createSurfApiConfig,
  ensureSurfClientConfig,
  getPdfPresignedUrl,
} from "../../services/surfClientConfig";

type Props = {
  style: SurfStyle;
  presentationType: string;
  previewSeed?: { title: string; subtitle?: string };
  onStyleChange: (style: SurfStyle) => void;
  onStyleConfirmed?: (confirmed: boolean) => void;
};

function initialPreviewTexts(
  presentationType: string,
  stylePresentationType: string | undefined,
  previewSeed?: { title: string; subtitle?: string },
) {
  const sample = previewSampleForPresentationType(presentationType || stylePresentationType);
  if (previewSeed?.title?.trim()) {
    return {
      title: previewSeed.title.trim(),
      subtitle: previewSeed.subtitle?.trim() ?? sample.subtitle,
      kicker: sample.kicker,
    };
  }
  return { title: sample.title, subtitle: sample.subtitle, kicker: sample.kicker };
}

function buildPreviewSubject(title: string, subtitle?: string): string {
  const parts = [title.trim(), subtitle?.trim()].filter(Boolean);
  return parts.join(" — ").slice(0, 400) || "abstract concept representing the deck topic";
}

export function StyleStep({
  style,
  presentationType,
  previewSeed,
  onStyleChange,
  onStyleConfirmed,
}: Props) {
  const [selectedLook, setSelectedLook] = useState(
    style.selectedPresetId ?? DEFAULT_PULSE_PRESET_ID,
  );
  const [previewTexts, setPreviewTexts] = useState(() =>
    initialPreviewTexts(presentationType, style.presentationType, previewSeed),
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [styleAccepted, setStyleAccepted] = useState(false);
  const [templates, setTemplates] = useState<SurfStyleTemplateRecord[]>([]);
  const [templateTitle, setTemplateTitle] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { run, busy } = useAsyncAction();

  const commType = presentationType || style.presentationType || "";
  const aspects = style.styleAspects ?? defaultStyleAspects();
  const { bg: paletteBg, tx: paletteTx } = derivePaletteFromStyle(style);
  const legible = isSurfPreviewLegible(style);

  const loadTemplates = async () => {
    await ensureSurfClientConfig();
    setTemplates(await listSurfStyleTemplates(createSurfApiConfig()));
  };

  useEffect(() => {
    void loadTemplates().catch(() => undefined);
  }, []);

  useEffect(() => {
    onStyleConfirmed?.(styleAccepted);
  }, [styleAccepted, onStyleConfirmed]);

  const applyDraft = (next: SurfStyle) => {
    setStyleAccepted(false);
    setPreviewUrl(null);
    onStyleChange({
      ...next,
      presentationType: commType || next.presentationType,
      composition: next.composition ?? "composed-background",
      styleAspects: next.styleAspects ?? defaultStyleAspects(),
    });
  };

  const generatePreviewBackground = async () => {
    const cfg = createSurfApiConfig();
    const subject = buildPreviewSubject(previewTexts.title, previewTexts.subtitle);
    const { outputAssetKey } = await generateSurfStylePreviewImage(cfg, style, subject);
    const presigned = getPdfPresignedUrl();
    if (!presigned) throw new Error("Storage non configurato per anteprima.");
    const url = await resolveSurfPreviewImageUrl(presigned, () => cfg.getIdToken(), outputAssetKey);
    setPreviewUrl(url);
    onStyleChange({
      ...style,
      previewS3Key: outputAssetKey,
      presentationType: commType || style.presentationType,
    });
  };

  const patchAspects = (patch: Partial<typeof aspects>) => {
    applyDraft({
      ...style,
      styleAspects: { ...aspects, ...patch },
    });
  };

  const onLookChange = (value: string) => {
    if (value.startsWith("template:")) {
      const id = value.slice("template:".length);
      const t = templates.find((x) => x.documentId === id);
      if (t) {
        applyDraft({
          ...t.style,
          presentationType: commType || t.style.presentationType,
        });
        if (t.presetId) setSelectedLook(t.presetId);
      }
      return;
    }
    setSelectedLook(value);
    applyDraft(styleFromPreset(value, commType || undefined));
  };

  const expandSelectedPreset = () =>
    run("Generazione stile…", async () => {
      const cfg = createSurfApiConfig();
      const next = await expandSurfStylePreset(cfg, selectedLook, commType || undefined);
      applyDraft(next);
      await generatePreviewBackground();
    });

  return (
    <section className="wizard-panel style-panel">
      <h2>Stile</h2>
      <p className="muted">
        Look di partenza, palette editabile e anteprima con testi modificabili — come in Pulse.
      </p>

      <label className="field-label">
        Tipo comunicazione
        <select
          value={commType}
          onChange={(e) =>
            onStyleChange({ ...style, presentationType: e.target.value || undefined })
          }
        >
          <option value="">— automatico —</option>
          {SURF_PRESENTATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <div className="style-pulse-layout">
        <article className="surf-card style-block">
          <header className="style-block-head">
            <h3>Look di partenza</h3>
            <div className="style-block-actions">
              <input
                type="text"
                className="template-title-inline"
                placeholder="Nome template"
                value={templateTitle}
                aria-label="Nome template da salvare"
                onChange={(e) => setTemplateTitle(e.target.value)}
              />
              <button
                type="button"
                className="surf-btn"
                disabled={busy || !templateTitle.trim()}
                onClick={() =>
                  void run("Salvataggio template…", async () => {
                    await saveSurfStyleTemplate(
                      createSurfApiConfig(),
                      templateTitle.trim(),
                      style,
                      selectedLook,
                    );
                    setTemplateTitle("");
                    await loadTemplates();
                  })
                }
              >
                Salva template
              </button>
            </div>
          </header>
          <label className="field-label">
            Scegli look
            <select value={selectedLook} onChange={(e) => onLookChange(e.target.value)}>
              {templates.length > 0 && (
                <optgroup label="I tuoi template">
                  {templates.map((t) => (
                    <option key={t.documentId} value={`template:${t.documentId}`}>
                      {t.title}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Suggerisci stile da preset">
                {PULSE_PRESET_CATALOG.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.emoji} {p.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          {templates.length > 0 && (
            <ul className="template-list template-list--compact">
              {templates.map((t) => (
                <li key={t.documentId}>
                  <span>{t.title}</span>
                  <button
                    type="button"
                    className="danger-link"
                    disabled={busy}
                    onClick={() =>
                      void run("Eliminazione template…", async () => {
                        await deleteSurfStyleTemplate(createSurfApiConfig(), t.documentId);
                        await loadTemplates();
                      })
                    }
                  >
                    Elimina
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="wizard-actions">
            <button
              type="button"
              className="primary-btn"
              disabled={busy || selectedLook.startsWith("template:")}
              onClick={() => void expandSelectedPreset()}
            >
              {busy ? "Generazione…" : "Genera stile (AI)"}
            </button>
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}>
              Analizza immagine…
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                void run("Analisi immagine…", async () => {
                  const dataUrl = await readFileAsDataUrl(file);
                  const next = await analyzeSurfStyleReference(
                    createSurfApiConfig(),
                    style,
                    dataUrl,
                  );
                  applyDraft(next);
                });
                e.target.value = "";
              }}
            />
          </div>
        </article>

        <article className="surf-card style-block">
          <h3>Colori</h3>
          <PaletteEditor
            backgroundColors={paletteBg}
            textColors={paletteTx}
            onBackgroundChange={(colors) => applyDraft(applyPaletteArrays(style, colors, paletteTx))}
            onTextChange={(colors) => applyDraft(applyPaletteArrays(style, paletteBg, colors))}
          />
          <details
            className="style-advanced"
            open={advancedOpen}
            onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}
          >
            <summary>Avanzate</summary>
            <label className="row">
              <input
                type="checkbox"
                checked={aspects.injectDerivedPromptInImagePrompt !== false}
                onChange={(e) => patchAspects({ injectDerivedPromptInImagePrompt: e.target.checked })}
              />
              Inietta derivedPrompt nei prompt immagine
            </label>
            <label className="row">
              <input
                type="checkbox"
                checked={aspects.injectPaletteInPrompt !== false}
                onChange={(e) => patchAspects({ injectPaletteInPrompt: e.target.checked })}
              />
              Inietta palette nei prompt
            </label>
            <label className="row">
              <input
                type="checkbox"
                checked={aspects.injectBackgroundPalette !== false}
                onChange={(e) => patchAspects({ injectBackgroundPalette: e.target.checked })}
              />
              Segmento palette background
            </label>
            <label className="row">
              <input
                type="checkbox"
                checked={aspects.injectTextPalette !== false}
                onChange={(e) => patchAspects({ injectTextPalette: e.target.checked })}
              />
              Segmento palette testo
            </label>
            <label className="field-label">
              derivedPrompt (look-only)
              <textarea
                rows={3}
                value={style.derivedPrompt ?? style.backgroundTreatment}
                onChange={(e) =>
                  applyDraft({
                    ...style,
                    derivedPrompt: e.target.value,
                    backgroundTreatment: e.target.value,
                  })
                }
              />
            </label>
          </details>
        </article>

        <article className="surf-card style-block style-block--preview">
          <h3>Anteprima stile (bozza)</h3>
          <StylePreviewFrame
            style={style}
            previewImageUrl={previewUrl}
            presentationType={commType}
            previewTexts={previewTexts}
            onPreviewTextChange={(patch) => setPreviewTexts((prev) => ({ ...prev, ...patch }))}
          />
          <p className="muted style-legibility-badge">
            Leggibilità: {style.legibility.strategy}
            {style.legibility.autoContrast ? " · autoContrast" : ""}
            {legible ? " · OK" : " · verifica contrasto"}
          </p>
          <div className="wizard-actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => void run("Anteprima AI…", () => generatePreviewBackground())}
            >
              Rigenera anteprima draft
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={busy || !legible}
              onClick={() => {
                setStyleAccepted(true);
                onStyleConfirmed?.(true);
              }}
            >
              Usa stile
            </button>
            {styleAccepted && <span className="ok">Stile confermato ✓</span>}
          </div>
        </article>
      </div>

      {templates.length === 0 && (
        <EmptyState
          title="Nessun template salvato"
          description="Genera uno stile e salvalo come template per riusarlo."
        />
      )}
    </section>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("Lettura file fallita."));
    reader.readAsDataURL(file);
  });
}
